import { Optional } from "common/utils/types";
import { US_STATES } from "common/constants/address";

import { useFeatures } from "client/clinics/hooks";
import TextButton from "client/common/components/buttons/TextButton";

import { useControlledSubstanceGuidanceDrawer } from "../stackables";
import { ControlledSubstanceClassification } from "../types";
import { getControlledProductLinesText } from "../utils";

type ControlledSubstanceStateNoticeProps = {
  summary: Optional<ControlledSubstanceClassification>;
  productLineNames: string[];
};

/**
 * Names the state a prescription's controlled-substance rules are judged against, so the
 * client can tell that changing where the order goes can change what is required of it.
 *
 * Renders nothing off the destination basis: `basis_state` is then the client's own
 * primary address, which no delivery address can change, so the sentence would promise a
 * variability that does not exist.
 */
const ControlledSubstanceStateNotice = ({
  summary,
  productLineNames,
}: ControlledSubstanceStateNoticeProps) => {
  const isDestinationBasisEnabled = useFeatures().CONTROLLED_SUBSTANCE_SHIPPING_ADDRESS_BASIS;
  const { open: openControlledSubstanceGuidanceDrawer } = useControlledSubstanceGuidanceDrawer();

  const stateName = summary?.basis_state ? US_STATES[summary.basis_state] : undefined;
  if (
    !isDestinationBasisEnabled ||
    !summary?.is_controlled ||
    !stateName ||
    !productLineNames.length
  ) {
    return null;
  }

  return (
    <>
      {getControlledProductLinesText(productLineNames)} in {stateName}. Prescription requirements
      may vary depending on your shipping address.{"\u00a0"}
      <TextButton hasUnderline={true} icon="right" onClick={openControlledSubstanceGuidanceDrawer}>
        Learn more
      </TextButton>
    </>
  );
};

export default ControlledSubstanceStateNotice;
